import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Search, CheckCircle, XCircle, Clock, 
  FileText, Building2, Newspaper, Loader2, ExternalLink,
  AlertTriangle, Info, CreditCard, Receipt, History,
  ChevronRight, ChevronDown, X, Upload, Link as LinkIcon, BanknoteIcon, TrendingUp, Wallet,
  Calendar, MapPin, Ticket, ShoppingCart, User, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';

const SettlementsPage = () => {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ status: '', entity_type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'balances'
  const [pendingBalances, setPendingBalances] = useState([]);
  const [balancesSummary, setBalancesSummary] = useState(null);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [showAllEntities, setShowAllEntities] = useState(false);
  const [expandedEntity, setExpandedEntity] = useState(null); // Track which entity is expanded
  
  // Modal states
  const [payModal, setPayModal] = useState({ open: false, settlement: null });
  const [rejectModal, setRejectModal] = useState({ open: false, settlement: null });
  const [detailModal, setDetailModal] = useState({ open: false, settlement: null });
  const [proactivePayModal, setProactivePayModal] = useState({ open: false, entity: null });
  const [paymentProofUrl, setPaymentProofUrl] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [proactiveAmount, setProactiveAmount] = useState('');

  useEffect(() => {
    fetchSettlements();
    fetchStats();
  }, [filter]);

  useEffect(() => {
    fetchPendingBalances();
  }, [showAllEntities]);

  const fetchSettlements = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.entity_type) params.append('entity_type', filter.entity_type);
      
      const response = await api.get(`/settlements/admin/all?${params}`);
      setSettlements(response.data.settlements || []);
    } catch (error) {
      toast.error('Error al cargar liquidaciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/settlements/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPendingBalances = async () => {
    try {
      setLoadingBalances(true);
      const params = new URLSearchParams();
      if (showAllEntities) params.append('include_zero', 'true');
      const response = await api.get(`/settlements/admin/pending-balances?${params}`);
      setPendingBalances(response.data.pending_balances || []);
      setBalancesSummary(response.data.summary || null);
    } catch (error) {
      console.error('Error fetching pending balances:', error);
    } finally {
      setLoadingBalances(false);
    }
  };

  const handleProactivePay = async () => {
    const amount = parseFloat(proactiveAmount);
    if (!amount || amount <= 0) {
      toast.error('Ingresá un monto válido');
      return;
    }
    
    if (!paymentProofUrl.trim()) {
      toast.error('El comprobante de pago es obligatorio');
      return;
    }
    
    if (!paymentProofUrl.startsWith('http://') && !paymentProofUrl.startsWith('https://')) {
      toast.error('Ingresá una URL válida del comprobante');
      return;
    }
    
    if (amount > (proactivePayModal.entity?.available_balance || 0)) {
      toast.error(`El monto excede el balance disponible`);
      return;
    }
    
    try {
      setProcessingId('proactive');
      const params = new URLSearchParams();
      params.append('entity_type', proactivePayModal.entity.entity_type);
      params.append('entity_id', proactivePayModal.entity.entity_id);
      params.append('amount', amount.toString());
      params.append('payment_proof_url', paymentProofUrl.trim());
      if (adminNotes.trim()) {
        params.append('admin_notes', adminNotes.trim());
      }
      
      await api.post(`/settlements/admin/create-settlement?${params}`);
      toast.success('Liquidación creada y pagada exitosamente');
      setProactivePayModal({ open: false, entity: null });
      setProactiveAmount('');
      setPaymentProofUrl('');
      setAdminNotes('');
      fetchSettlements();
      fetchStats();
      fetchPendingBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear liquidación');
    } finally {
      setProcessingId(null);
    }
  };

  const handleApprove = async (settlementId) => {
    try {
      setProcessingId(settlementId);
      await api.put(`/settlements/admin/${settlementId}/approve`);
      toast.success('Liquidación aprobada - Se envió email de notificación');
      fetchSettlements();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally {
      setProcessingId(null);
    }
  };

  const openPayModal = (settlement) => {
    setPayModal({ open: true, settlement });
    setPaymentProofUrl('');
    setAdminNotes('');
  };

  const handleMarkPaid = async () => {
    if (!paymentProofUrl.trim()) {
      toast.error('El comprobante de pago es obligatorio');
      return;
    }
    
    if (!paymentProofUrl.startsWith('http://') && !paymentProofUrl.startsWith('https://')) {
      toast.error('Ingresá una URL válida del comprobante');
      return;
    }
    
    try {
      setProcessingId(payModal.settlement.id);
      const params = new URLSearchParams();
      params.append('payment_proof_url', paymentProofUrl.trim());
      if (adminNotes.trim()) {
        params.append('admin_notes', adminNotes.trim());
      }
      
      await api.put(`/settlements/admin/${payModal.settlement.id}/pay?${params}`);
      toast.success('Liquidación pagada - Se envió email con comprobante');
      setPayModal({ open: false, settlement: null });
      fetchSettlements();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (settlement) => {
    setRejectModal({ open: true, settlement });
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('El motivo del rechazo es obligatorio');
      return;
    }
    
    try {
      setProcessingId(rejectModal.settlement.id);
      await api.put(`/settlements/admin/${rejectModal.settlement.id}/reject?admin_notes=${encodeURIComponent(rejectReason.trim())}`);
      toast.success('Liquidación rechazada - Se envió email de notificación');
      setRejectModal({ open: false, settlement: null });
      fetchSettlements();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { 
        bg: 'bg-amber-500/10', 
        border: 'border-amber-500/30', 
        text: 'text-amber-400',
        icon: Clock,
        label: 'Pendiente'
      },
      approved: { 
        bg: 'bg-blue-500/10', 
        border: 'border-blue-500/30', 
        text: 'text-blue-400',
        icon: CheckCircle,
        label: 'Aprobada'
      },
      paid: { 
        bg: 'bg-emerald-500/10', 
        border: 'border-emerald-500/30', 
        text: 'text-emerald-400',
        icon: BanknoteIcon,
        label: 'Pagada'
      },
      rejected: { 
        bg: 'bg-red-500/10', 
        border: 'border-red-500/30', 
        text: 'text-red-400',
        icon: XCircle,
        label: 'Rechazada'
      }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${c.bg} ${c.border} ${c.text}`}>
        <Icon size={12} />
        {c.label}
      </span>
    );
  };

  const filteredSettlements = settlements.filter(s => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      s.entity_name?.toLowerCase().includes(search) ||
      s.event_title?.toLowerCase().includes(search) ||
      s.id?.toLowerCase().includes(search)
    );
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPending = stats?.totals?.pending || 0;
  const totalApproved = stats?.totals?.approved || 0;
  const totalPaid = stats?.totals?.paid || 0;

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="space-y-6" data-testid="settlements-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <DollarSign className="text-white" size={22} />
            </div>
            Liquidaciones
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Gestión de pagos a productoras y proveedores de servicios
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
        <Info className="text-blue-400 mt-0.5 flex-shrink-0" size={20} />
        <div>
          <p className="text-blue-300 font-medium text-sm">Información Importante</p>
          <p className="text-blue-200/70 text-sm mt-1">
            Los montos mostrados son <strong>ingresos netos</strong> (sin costos de servicio). Los pagos pueden demorar entre <strong>24 y 48 horas hábiles</strong> en acreditarse. 
            El comprobante de pago es <strong>obligatorio</strong>.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-4">
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            activeTab === 'requests'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
          }`}
        >
          <Receipt className="inline-block mr-2" size={16} />
          Solicitudes ({settlements.length})
        </button>
        <button
          onClick={() => setActiveTab('balances')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            activeTab === 'balances'
              ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
          }`}
        >
          <Wallet className="inline-block mr-2" size={16} />
          Balances Pendientes ({pendingBalances.length})
        </button>
      </div>

      {activeTab === 'requests' && (
        <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-400 text-xs font-semibold uppercase tracking-wide">Pendientes</p>
              <p className="text-3xl font-black text-white mt-1">{stats?.counts?.pending || 0}</p>
              <p className="text-amber-400/70 text-sm mt-1">{formatCurrency(totalPending)}</p>
            </div>
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Clock className="text-amber-400" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-400 text-xs font-semibold uppercase tracking-wide">Aprobadas</p>
              <p className="text-3xl font-black text-white mt-1">{stats?.counts?.approved || 0}</p>
              <p className="text-blue-400/70 text-sm mt-1">{formatCurrency(totalApproved)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <CheckCircle className="text-blue-400" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">Pagadas</p>
              <p className="text-3xl font-black text-white mt-1">{stats?.counts?.paid || 0}</p>
              <p className="text-emerald-400/70 text-sm mt-1">{formatCurrency(totalPaid)}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <BanknoteIcon className="text-emerald-400" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-400 text-xs font-semibold uppercase tracking-wide">Rechazadas</p>
              <p className="text-3xl font-black text-white mt-1">{stats?.counts?.rejected || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
              <XCircle className="text-red-400" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input
              placeholder="Buscar por nombre, evento o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-800/50 border-zinc-700"
            />
          </div>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobadas</option>
            <option value="paid">Pagadas</option>
            <option value="rejected">Rechazadas</option>
          </select>
          <select
            value={filter.entity_type}
            onChange={(e) => setFilter({ ...filter, entity_type: e.target.value })}
            className="bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          >
            <option value="">Todos los tipos</option>
            <option value="producer">Productoras</option>
            <option value="press">Servicios</option>
          </select>
        </div>
      </div>

      {/* Settlements List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800 rounded-xl">
            <Loader2 className="animate-spin text-emerald-500" size={32} />
          </div>
        ) : filteredSettlements.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800 rounded-xl text-zinc-500">
            <DollarSign size={48} className="mb-4 opacity-50" />
            <p className="font-medium">No hay liquidaciones para mostrar</p>
            <p className="text-sm mt-1">Las solicitudes aparecerán aquí</p>
          </div>
        ) : (
          filteredSettlements.map((settlement) => (
            <div 
              key={settlement.id} 
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      settlement.entity_type === 'producer' 
                        ? 'bg-violet-500/20' 
                        : 'bg-blue-500/20'
                    }`}>
                      {settlement.entity_type === 'producer' ? (
                        <Building2 className="text-violet-400" size={20} />
                      ) : (
                        <Newspaper className="text-blue-400" size={20} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-white truncate">{settlement.entity_name}</p>
                      <p className="text-xs text-zinc-500">
                        {settlement.entity_type === 'producer' ? 'Productora' : 'Proveedor de Servicios'} • {settlement.id}
                      </p>
                    </div>
                  </div>
                  
                  {settlement.event_title && (
                    <p className="text-sm text-zinc-400 ml-13 pl-13">
                      Evento: <span className="text-zinc-300">{settlement.event_title}</span>
                    </p>
                  )}
                </div>
                
                {/* Amount */}
                <div className="text-center lg:text-right">
                  <p className="text-2xl font-black text-emerald-400">{formatCurrency(settlement.amount)}</p>
                  <p className="text-xs text-zinc-500">{formatDate(settlement.requested_at)}</p>
                </div>
                
                {/* Status */}
                <div className="flex items-center gap-3">
                  {getStatusBadge(settlement.status)}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {settlement.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"
                        onClick={() => handleApprove(settlement.id)}
                        disabled={processingId === settlement.id}
                      >
                        {processingId === settlement.id ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <>
                            <CheckCircle size={14} className="mr-1" />
                            Aprobar
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        onClick={() => openRejectModal(settlement)}
                        disabled={processingId === settlement.id}
                      >
                        <XCircle size={14} className="mr-1" />
                        Rechazar
                      </Button>
                    </>
                  )}
                  
                  {settlement.status === 'approved' && (
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-lg shadow-emerald-500/20"
                      onClick={() => openPayModal(settlement)}
                      disabled={processingId === settlement.id}
                    >
                      {processingId === settlement.id ? (
                        <Loader2 className="animate-spin" size={14} />
                      ) : (
                        <>
                          <CreditCard size={14} className="mr-1" />
                          Registrar Pago
                        </>
                      )}
                    </Button>
                  )}
                  
                  {settlement.payment_proof_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                      onClick={() => window.open(settlement.payment_proof_url, '_blank')}
                    >
                      <Receipt size={14} className="mr-1" />
                      Comprobante
                    </Button>
                  )}
                  
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-zinc-400 hover:text-white"
                    onClick={() => setDetailModal({ open: true, settlement })}
                  >
                    <ChevronRight size={18} />
                  </Button>
                </div>
              </div>
              
              {/* Admin Notes / Rejection Reason */}
              {settlement.admin_notes && settlement.status === 'rejected' && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-xs text-red-400 font-semibold mb-1">Motivo del rechazo:</p>
                  <p className="text-sm text-red-300">{settlement.admin_notes}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
        </>
      )}

      {/* Balances Pendientes Tab */}
      {activeTab === 'balances' && (
        <>
          {/* Balances Summary */}
          {balancesSummary && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-violet-500/10 to-violet-600/5 border border-violet-500/20 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-violet-400 text-xs font-semibold uppercase tracking-wide">Total a Liquidar</p>
                    <p className="text-3xl font-black text-white mt-1">{formatCurrency(balancesSummary.total_pending_amount)}</p>
                    <p className="text-violet-400/70 text-sm mt-1">{balancesSummary.total_entities} entidades</p>
                  </div>
                  <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center">
                    <Wallet className="text-violet-400" size={24} />
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-400 text-xs font-semibold uppercase tracking-wide">Productoras</p>
                    <p className="text-3xl font-black text-white mt-1">{formatCurrency(balancesSummary.producers_pending_amount)}</p>
                    <p className="text-amber-400/70 text-sm mt-1">{balancesSummary.producers_count} productoras</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <Building2 className="text-amber-400" size={24} />
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-400 text-xs font-semibold uppercase tracking-wide">Servicios</p>
                    <p className="text-3xl font-black text-white mt-1">{formatCurrency(balancesSummary.services_pending_amount)}</p>
                    <p className="text-blue-400/70 text-sm mt-1">{balancesSummary.services_count} proveedores</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Newspaper className="text-blue-400" size={24} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pending Balances List */}
          <div className="space-y-3">
            {/* Toggle for showing all entities */}
            <div className="flex items-center justify-between p-4 bg-zinc-800/50 border border-zinc-700 rounded-xl mb-4">
              <div className="flex items-center gap-3">
                <Info size={18} className="text-blue-400" />
                <div>
                  <p className="text-sm font-medium text-white">Ver todas las entidades</p>
                  <p className="text-xs text-zinc-400">Incluye entidades con balance 0 o negativo (para diagnóstico)</p>
                </div>
              </div>
              <button
                onClick={() => setShowAllEntities(!showAllEntities)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showAllEntities ? 'bg-violet-600' : 'bg-zinc-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showAllEntities ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            
            {loadingBalances ? (
              <div className="flex items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800 rounded-xl">
                <Loader2 className="animate-spin text-violet-500" size={32} />
              </div>
            ) : pendingBalances.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800 rounded-xl text-zinc-500">
                <CheckCircle size={48} className="mb-4 opacity-50 text-emerald-500" />
                <p className="font-medium text-emerald-400">¡Todo liquidado!</p>
                <p className="text-sm mt-1">No hay balances pendientes de pago</p>
              </div>
            ) : (
              pendingBalances.map((entity) => (
                <div 
                  key={`${entity.entity_type}-${entity.entity_id}`}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          entity.entity_type === 'producer' 
                            ? 'bg-amber-500/20' 
                            : 'bg-blue-500/20'
                        }`}>
                          {entity.entity_type === 'producer' ? (
                            <Building2 className="text-amber-400" size={20} />
                          ) : (
                            <Newspaper className="text-blue-400" size={20} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-white truncate">{entity.entity_name}</p>
                          <p className="text-xs text-zinc-500">
                            {entity.entity_type === 'producer' ? 'Productora' : 'Proveedor de Servicios'} 
                            {entity.entity_email && ` • ${entity.entity_email}`}
                          </p>
                        </div>
                      </div>
                      
                      {/* Quick stats */}
                      <div className="flex gap-4 text-xs text-zinc-400 mt-2 ml-12">
                        {entity.entity_type === 'producer' && (
                          <>
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {entity.events_count || entity.earnings_detail?.length || 0} eventos
                            </span>
                          </>
                        )}
                        {entity.entity_type === 'press' && (
                          <span className="flex items-center gap-1">
                            <FileText size={12} />
                            {entity.contracts_count || entity.earnings_detail?.length || 0} contratos
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Balance */}
                    <div className="text-center lg:text-right">
                      <p className="text-xs text-zinc-500">Disponible para liquidar</p>
                      <p className={`text-2xl font-black ${
                        entity.available_balance > 0 ? 'text-emerald-400' : 
                        entity.available_balance < 0 ? 'text-red-400' : 'text-zinc-500'
                      }`}>
                        {formatCurrency(entity.available_balance)}
                      </p>
                      <p className="text-xs text-zinc-500">
                        Total generado: {formatCurrency(entity.total_earned)} | Liquidado: {formatCurrency(entity.total_settled)}
                      </p>
                      {entity.available_balance < 0 && (
                        <p className="text-xs text-red-400 mt-1">
                          ⚠️ Se liquidó más del neto (posible error histórico)
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 hover:bg-zinc-800"
                        onClick={() => setExpandedEntity(expandedEntity === entity.entity_id ? null : entity.entity_id)}
                      >
                        <Eye size={14} className="mr-1" />
                        {expandedEntity === entity.entity_id ? 'Ocultar' : 'Ver Detalle'}
                        {expandedEntity === entity.entity_id ? <ChevronDown size={14} className="ml-1" /> : <ChevronRight size={14} className="ml-1" />}
                      </Button>
                      {entity.available_balance > 0 && (
                        <Button
                          className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-lg shadow-emerald-500/20"
                          onClick={() => {
                            setProactivePayModal({ open: true, entity });
                            setProactiveAmount(entity.available_balance.toString());
                            setPaymentProofUrl('');
                            setAdminNotes('');
                          }}
                        >
                          <CreditCard size={16} className="mr-2" />
                          Pagar
                        </Button>
                      )}
                      {entity.available_balance <= 0 && (
                        <span className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          entity.available_balance < 0 
                            ? 'bg-red-500/10 text-red-400 border border-red-500/20' 
                            : 'bg-zinc-700 text-zinc-400'
                        }`}>
                          {entity.available_balance < 0 ? 'Revisar' : 'Sin balance'}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedEntity === entity.entity_id && entity.earnings_detail?.length > 0 && (
                    <div className="border-t border-zinc-800 p-4 bg-zinc-900/50">
                      <h4 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
                        <Receipt size={14} className="text-amber-400" />
                        {entity.entity_type === 'producer' ? 'Detalle por Evento' : 'Detalle por Contrato'}
                      </h4>
                      
                      <div className="space-y-3">
                        {entity.earnings_detail.map((item, idx) => (
                          <div key={idx} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                              {/* Event/Contract Info */}
                              <div className="flex-1">
                                <p className="font-medium text-white">{item.event_title}</p>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-zinc-400">
                                  {item.event_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar size={10} />
                                      {item.event_date}
                                    </span>
                                  )}
                                  {item.event_location && (
                                    <span className="flex items-center gap-1">
                                      <MapPin size={10} />
                                      {item.event_location}
                                    </span>
                                  )}
                                  {entity.entity_type === 'producer' && item.tickets_sold > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Ticket size={10} />
                                      {item.tickets_sold} tickets vendidos
                                    </span>
                                  )}
                                  {entity.entity_type === 'producer' && item.orders_count > 0 && (
                                    <span className="flex items-center gap-1">
                                      <ShoppingCart size={10} />
                                      {item.orders_count} órdenes
                                    </span>
                                  )}
                                  {entity.entity_type === 'press' && item.service_type && (
                                    <span className="flex items-center gap-1">
                                      <FileText size={10} />
                                      {item.service_type}
                                    </span>
                                  )}
                                  {entity.entity_type === 'press' && item.producer_name && (
                                    <span className="flex items-center gap-1">
                                      <User size={10} />
                                      {item.producer_name}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Financial breakdown for producers */}
                                {entity.entity_type === 'producer' && (item.total_gross > 0 || item.total_service_fees > 0) && (
                                  <div className="mt-2 p-2 bg-zinc-900 rounded text-xs">
                                    <div className="flex justify-between text-zinc-400">
                                      <span>Venta bruta:</span>
                                      <span className="text-zinc-300">{formatCurrency(item.total_gross)}</span>
                                    </div>
                                    <div className="flex justify-between text-zinc-500">
                                      <span>- Costos de servicio:</span>
                                      <span>{formatCurrency(item.total_service_fees)}</span>
                                    </div>
                                    {(item.total_discounts || 0) > 0 && (
                                      <div className="flex justify-between text-amber-400" data-testid={`settlement-discount-${item.event_id}`}>
                                        <span>- Descuentos aplicados:</span>
                                        <span>{formatCurrency(item.total_discounts)}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between text-emerald-400 font-medium border-t border-zinc-700 pt-1 mt-1">
                                      <span>= Neto para productor:</span>
                                      <span>{formatCurrency(item.total_earned)}</span>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Settlement history */}
                                {item.settlement_history?.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-zinc-500 mb-1">Liquidaciones anteriores:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {item.settlement_history.map((sh, shIdx) => (
                                        <span key={shIdx} className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded">
                                          {formatCurrency(sh.amount)} ({sh.date || 'sin fecha'})
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Amounts */}
                              <div className="flex flex-row lg:flex-col gap-4 lg:gap-1 lg:text-right">
                                <div>
                                  <p className="text-xs text-zinc-500">Generado</p>
                                  <p className="font-semibold text-white">{formatCurrency(item.total_earned)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-zinc-500">Liquidado</p>
                                  <p className="font-semibold text-zinc-400">{formatCurrency(item.already_settled)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-zinc-500">Disponible</p>
                                  <p className={`font-bold ${item.available > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                                    {formatCurrency(item.available)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Proactive Pay Modal */}
      <Dialog open={proactivePayModal.open} onOpenChange={(open) => !open && setProactivePayModal({ open: false, entity: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <CreditCard className="text-emerald-400" size={20} />
              Pago Proactivo
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Crear liquidación y registrar pago
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Entity Info */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  proactivePayModal.entity?.entity_type === 'producer' 
                    ? 'bg-amber-500/20' 
                    : 'bg-blue-500/20'
                }`}>
                  {proactivePayModal.entity?.entity_type === 'producer' ? (
                    <Building2 className="text-amber-400" size={20} />
                  ) : (
                    <Newspaper className="text-blue-400" size={20} />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-white">{proactivePayModal.entity?.entity_name}</p>
                  <p className="text-xs text-zinc-500">{proactivePayModal.entity?.entity_email}</p>
                </div>
              </div>
            </div>
            
            {/* Available Balance */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
              <p className="text-xs text-emerald-400 uppercase tracking-wide">Balance Disponible</p>
              <p className="text-3xl font-black text-emerald-400 mt-1">
                {formatCurrency(proactivePayModal.entity?.available_balance)}
              </p>
            </div>
            
            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Monto a pagar
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={proactiveAmount}
                    onChange={(e) => setProactiveAmount(e.target.value)}
                    placeholder="0"
                    max={proactivePayModal.entity?.available_balance}
                    className="pl-8 bg-zinc-800 border-zinc-700 text-lg font-bold"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setProactiveAmount(proactivePayModal.entity?.available_balance?.toString() || '0')}
                  className="border-zinc-700"
                >
                  Todo
                </Button>
              </div>
            </div>
            
            {/* Payment Proof URL */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <span className="text-red-400">*</span> URL del Comprobante de Pago
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <Input
                  placeholder="https://..."
                  value={paymentProofUrl}
                  onChange={(e) => setPaymentProofUrl(e.target.value)}
                  className="pl-10 bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            {/* Admin Notes */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Notas (opcional)
              </label>
              <Textarea
                placeholder="Notas adicionales..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="bg-zinc-800 border-zinc-700 resize-none"
                rows={2}
              />
            </div>
            
            {/* Warning */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={16} />
              <p className="text-xs text-amber-300">
                Esto creará una liquidación y la marcará como pagada inmediatamente. Se enviará un email al beneficiario con el comprobante.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setProactivePayModal({ open: false, entity: null })}
              className="border-zinc-700"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleProactivePay}
              disabled={!proactiveAmount || !paymentProofUrl.trim() || processingId === 'proactive'}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {processingId === 'proactive' ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <CheckCircle className="mr-2" size={16} />
              )}
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Modal */}
      <Dialog open={payModal.open} onOpenChange={(open) => !open && setPayModal({ open: false, settlement: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <CreditCard className="text-emerald-400" size={20} />
              Registrar Pago
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Ingresá el comprobante de la transferencia realizada
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Amount Display */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
              <p className="text-xs text-emerald-400 uppercase tracking-wide">Monto a Pagar</p>
              <p className="text-3xl font-black text-emerald-400 mt-1">
                {formatCurrency(payModal.settlement?.amount)}
              </p>
              <p className="text-sm text-zinc-400 mt-2">
                A: {payModal.settlement?.entity_name}
              </p>
            </div>
            
            {/* Payment Proof URL */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <span className="text-red-400">*</span> URL del Comprobante de Pago
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <Input
                  placeholder="https://..."
                  value={paymentProofUrl}
                  onChange={(e) => setPaymentProofUrl(e.target.value)}
                  className="pl-10 bg-zinc-800 border-zinc-700"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Subí el comprobante a un servicio como Cloudinary, Imgur o Google Drive y pegá el link
              </p>
            </div>
            
            {/* Admin Notes */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Notas (opcional)
              </label>
              <Textarea
                placeholder="Notas adicionales sobre el pago..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="bg-zinc-800 border-zinc-700 resize-none"
                rows={2}
              />
            </div>
            
            {/* Warning */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={16} />
              <p className="text-xs text-amber-300">
                Al confirmar, se enviará un email automático al beneficiario con el comprobante de pago adjunto.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPayModal({ open: false, settlement: null })}
              className="border-zinc-700"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleMarkPaid}
              disabled={!paymentProofUrl.trim() || processingId}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {processingId ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <CheckCircle className="mr-2" size={16} />
              )}
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={(open) => !open && setRejectModal({ open: false, settlement: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <XCircle className="text-red-400" size={20} />
              Rechazar Liquidación
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Indicá el motivo del rechazo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Amount Display */}
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center">
              <p className="text-xs text-red-400 uppercase tracking-wide">Monto Solicitado</p>
              <p className="text-3xl font-black text-red-400 mt-1">
                {formatCurrency(rejectModal.settlement?.amount)}
              </p>
              <p className="text-sm text-zinc-400 mt-2">
                Por: {rejectModal.settlement?.entity_name}
              </p>
            </div>
            
            {/* Rejection Reason */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <span className="text-red-400">*</span> Motivo del Rechazo
              </label>
              <Textarea
                placeholder="Explicá por qué se rechaza esta solicitud..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="bg-zinc-800 border-zinc-700 resize-none"
                rows={3}
              />
            </div>
            
            {/* Warning */}
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
              <p className="text-xs text-red-300">
                El beneficiario recibirá un email con el motivo del rechazo.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRejectModal({ open: false, settlement: null })}
              className="border-zinc-700"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleReject}
              disabled={!rejectReason.trim() || processingId}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {processingId ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <XCircle className="mr-2" size={16} />
              )}
              Confirmar Rechazo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModal.open} onOpenChange={(open) => !open && setDetailModal({ open: false, settlement: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <FileText className="text-emerald-400" size={20} />
              Detalle de Liquidación
            </DialogTitle>
          </DialogHeader>
          
          {detailModal.settlement && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">ID</p>
                  <p className="text-white font-mono">{detailModal.settlement.id}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">Estado</p>
                  <div className="mt-1">{getStatusBadge(detailModal.settlement.status)}</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">Monto</p>
                  <p className="text-emerald-400 font-bold text-lg">{formatCurrency(detailModal.settlement.amount)}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">Tipo</p>
                  <p className="text-white">{detailModal.settlement.entity_type === 'producer' ? 'Productora' : 'Servicio'}</p>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <p className="text-xs text-zinc-500 uppercase mb-1">Beneficiario</p>
                <p className="text-white font-medium">{detailModal.settlement.entity_name}</p>
                {detailModal.settlement.entity_email && (
                  <p className="text-zinc-400 text-sm">{detailModal.settlement.entity_email}</p>
                )}
              </div>
              
              {detailModal.settlement.event_title && (
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase mb-1">Evento</p>
                  <p className="text-white">{detailModal.settlement.event_title}</p>
                </div>
              )}
              
              <div className="border-t border-zinc-800 pt-4">
                <p className="text-xs text-zinc-500 uppercase mb-2 flex items-center gap-1">
                  <History size={12} /> Historial
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className="text-zinc-400">Solicitado:</span>
                    <span className="text-white">{formatDate(detailModal.settlement.requested_at)}</span>
                  </div>
                  {detailModal.settlement.approved_at && (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-blue-400" />
                      <span className="text-zinc-400">Aprobado:</span>
                      <span className="text-white">{formatDate(detailModal.settlement.approved_at)}</span>
                      {detailModal.settlement.approved_by && (
                        <span className="text-zinc-500">por {detailModal.settlement.approved_by}</span>
                      )}
                    </div>
                  )}
                  {detailModal.settlement.paid_at && (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-zinc-400">Pagado:</span>
                      <span className="text-white">{formatDate(detailModal.settlement.paid_at)}</span>
                      {detailModal.settlement.paid_by && (
                        <span className="text-zinc-500">por {detailModal.settlement.paid_by}</span>
                      )}
                    </div>
                  )}
                  {detailModal.settlement.rejected_at && (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <span className="text-zinc-400">Rechazado:</span>
                      <span className="text-white">{formatDate(detailModal.settlement.rejected_at)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {detailModal.settlement.payment_proof_url && (
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-500"
                  onClick={() => window.open(detailModal.settlement.payment_proof_url, '_blank')}
                >
                  <ExternalLink size={16} className="mr-2" />
                  Ver Comprobante de Pago
                </Button>
              )}
              
              {detailModal.settlement.admin_notes && (
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase mb-1">Notas del Admin</p>
                  <p className="text-zinc-300 text-sm">{detailModal.settlement.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default SettlementsPage;
